import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

import { logServerError } from '../utilities';

export const injectToken =
  (token: string) =>
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    config.headers.set('Authorization', `Bearer ${token}`);
    return config;
  };

export class Http {
  protected http!: AxiosInstance;
  private isInitialized = false;

  initializeHttp(
    baseURL: string,
    customHeaders?: Record<string, string | boolean>,
    authToken?: string,
    params?: Record<string, string | boolean>
  ) {
    this.http = axios.create({
      baseURL,
      withCredentials: !!authToken,
      params,
    });

    if (customHeaders) this.setCustomHeaders(customHeaders);
    if (authToken) this.initializeRequestInterceptor(authToken);
    this.initializeResponseInterceptor();

    this.isInitialized = true;
  }

  isReady(): boolean {
    return this.isInitialized;
  }

  private checkHttpInitialization() {
    if (!this.http) {
      throw new Error('HTTP client is not initialized.');
    }
  }

  private setCustomHeaders(customHeaders?: Record<string, string | boolean>) {
    this.http.defaults.headers = { ...this.http.defaults.headers, ...customHeaders };
  }

  protected initializeResponseInterceptor() {
    this.http.interceptors.response.use(
      (response) => response,
      (error) => {
        return this.handleError(error);
      }
    );
  }

  protected initializeRequestInterceptor(token: string) {
    this.http.interceptors.request.use(injectToken(token), (error) => Promise.reject(error));
  }

  get<T, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
    this.checkHttpInitialization();
    return this.http.get<T, R>(url, config);
  }

  delete<T, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
    this.checkHttpInitialization();
    return this.http.delete<T, R>(url, config);
  }

  post<T, R = AxiosResponse<T>>(url: string, data?: T, config?: AxiosRequestConfig): Promise<R> {
    this.checkHttpInitialization();
    return this.http.post<T, R>(url, data, config);
  }

  put<T, R = AxiosResponse<T>>(url: string, data?: T, config?: AxiosRequestConfig): Promise<R> {
    this.checkHttpInitialization();
    return this.http.put<T, R>(url, data, config);
  }

  patch<T, R = AxiosResponse<T>>(url: string, data?: T, config?: AxiosRequestConfig): Promise<R> {
    this.checkHttpInitialization();
    return this.http.patch<T, R>(url, data, config);
  }

  private handleError(error: Error) {
    logServerError(error);

    return Promise.reject(error);
  }
}
